import React, { useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PublicLayout from '../../components/site/PublicLayout';
import { useLanguage } from '../../context/LanguageContext';
import { langPath } from '../../utils/siteNav';

const COPY = {
  en: { title: 'Page not found', text: 'The page you are looking for does not exist or has moved.', home: 'Back to the homepage' },
  me: { title: 'Stranica nije pronađena', text: 'Stranica koju tražite ne postoji ili je premještena.', home: 'Nazad na početnu' },
  sq: { title: 'Faqja nuk u gjet', text: 'Faqja që kërkoni nuk ekziston ose është zhvendosur.', home: 'Kthehu në faqen kryesore' },
  de: { title: 'Seite nicht gefunden', text: 'Die gesuchte Seite existiert nicht oder wurde verschoben.', home: 'Zurück zur Startseite' },
  pl: { title: 'Strona nie znaleziona', text: 'Strona, której szukasz, nie istnieje lub została przeniesiona.', home: 'Wróć na stronę główną' },
  ru: { title: 'Страница не найдена', text: 'Страница, которую вы ищете, не существует или была перемещена.', home: 'На главную' },
  hu: { title: 'Az oldal nem található', text: 'A keresett oldal nem létezik vagy áthelyezték.', home: 'Vissza a főoldalra' },
  ro: { title: 'Pagina nu a fost găsită', text: 'Pagina pe care o căutați nu există sau a fost mutată.', home: 'Înapoi la pagina principală' },
};

export default function NotFoundPage() {
  const { language } = useLanguage();
  const { pathname, search, hash } = useLocation();
  const copy = COPY[language] || COPY.en;

  useEffect(() => {
    document.title = `${copy.title} | Hotel Uma Premium`;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [copy.title]);

  // English lives at the root — /en/... is a common guess, send it to the right place.
  if (pathname === '/en' || pathname.startsWith('/en/')) {
    return <Navigate to={(pathname.slice(3) || '/') + search + hash} replace />;
  }

  return (
    <PublicLayout>
      <main className="min-h-[70vh] bg-slate-900 flex flex-col items-center justify-center text-center px-6 pt-32 pb-20" data-testid="page-not-found">
        <p className="text-amber-400 text-xs font-semibold tracking-[0.3em] uppercase mb-4">404</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-white mb-4" data-testid="not-found-title">{copy.title}</h1>
        <p className="text-slate-300 max-w-md mb-8">{copy.text}</p>
        <Link to={langPath(language)} className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-full font-medium transition-colors" data-testid="not-found-home-link">
          <ArrowLeft className="w-4 h-4" />{copy.home}
        </Link>
      </main>
    </PublicLayout>
  );
}
